import type { ReportAnswerInput } from '@fieldops/shared'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  clearDeviceDraft,
  readDeviceDraft,
  writeDeviceDraft,
} from '../../lib/device-drafts.js'
import type { ReportAnswer, ReportAnswers } from './types.js'

/**
 * The answers as the technician has them, and getting them to the server.
 *
 * A report is filled in standing in a subfloor, so the screen never waits for
 * the network: a tap changes local state immediately and the save follows.
 * Saves are batched on a short idle timer for two reasons — a chip tap and the
 * next chip tap are one thought, and a textarea would otherwise send a request
 * per keystroke on the worst connection in the business.
 *
 * Only changed keys go, which is what `saveAnswers` is shaped for. The dirty set
 * is a ref rather than state because a keystroke during a flight must mark the
 * key dirty again without re-rendering, and the flush must see that mark.
 *
 * Failures are held, not dropped, in two places. In memory the same payload goes
 * again on the next change, on demand, or the moment the browser says the
 * connection is back. On the device it goes to IndexedDB, so the work survives
 * the tab being evicted while the phone is in a pocket — which is the whole
 * reason the header is allowed to say "Saved on device".
 */

export type DraftState = 'clean' | 'pending' | 'saving' | 'failed'

/** Long enough to collect a burst of taps, short enough to feel immediate. */
const IDLE_MS = 700

export type AnswerDraft = {
  answers: ReportAnswers
  set: (key: string, value: ReportAnswer) => void
  /**
   * Several answers at once, as one change.
   *
   * Accepting six suggestions is one act, so it is one save and one line in the
   * audit trail rather than six of each.
   */
  setMany: (values: Record<string, ReportAnswer>) => void
  /** Sends anything outstanding now, for the footer's Complete button. */
  flush: () => Promise<void>
  state: DraftState
  /** Whether anything has been typed that the server has not acknowledged. */
  outstanding: boolean
  /**
   * Whether what is outstanding is written to the device.
   *
   * Read by the header badge, which may only claim the answers are safe when
   * storage actually took them.
   */
  persisted: boolean
  /**
   * Answers recovered from the device that the server had not been told.
   *
   * Held for the life of the screen rather than cleared once they are sent. A
   * technician who does not know an answer reappeared cannot check it, and the
   * notice would otherwise flash past in the time one request takes.
   */
  recovered: number
  error: string | null
}

export function useAnswerDraft({
  responseId,
  serverAnswers,
  save,
}: {
  responseId: string | null
  serverAnswers: ReportAnswers
  save: (answers: Record<string, ReportAnswerInput>) => Promise<unknown>
}): AnswerDraft {
  const [answers, setAnswers] = useState<ReportAnswers>(serverAnswers)
  const [state, setState] = useState<DraftState>('clean')
  const [error, setError] = useState<string | null>(null)
  const [persisted, setPersisted] = useState(false)
  const [recovered, setRecovered] = useState(0)

  const dirty = useRef(new Set<string>())
  const latest = useRef<ReportAnswers>(serverAnswers)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const seeded = useRef<string | null>(null)

  // The server's copy seeds the screen once per report. Re-seeding on every
  // refetch would fight the technician: a save's own response arrives after the
  // next tap, and the field would jump back to what it held a moment ago.
  useEffect(() => {
    if (seeded.current === responseId) return
    seeded.current = responseId
    dirty.current.clear()
    latest.current = serverAnswers
    setAnswers(serverAnswers)
    setState('clean')
    setError(null)
    setPersisted(false)
    setRecovered(0)
  }, [responseId, serverAnswers])

  /**
   * Persisting to the device.
   *
   * Fire and forget on purpose. A tap must not wait on storage, and a refusal —
   * private browsing, an evicted origin — only means the badge cannot claim the
   * answers are held, not that the answer was lost.
   */
  const remember = useCallback(() => {
    if (responseId === null) return

    if (dirty.current.size === 0) {
      setPersisted(false)
      void clearDeviceDraft(responseId)
      return
    }

    void writeDeviceDraft({
      responseId,
      answers: latest.current,
      dirty: [...dirty.current],
    })
      .then(() => setPersisted(true))
      .catch(() => setPersisted(false))
  }, [responseId])

  const send = useCallback(async () => {
    if (responseId === null) return
    if (dirty.current.size === 0) return

    const keys = [...dirty.current]
    const payload: Record<string, ReportAnswerInput> = {}
    for (const key of keys) {
      const value = latest.current[key]
      payload[key] = value === undefined ? null : value
    }

    setState('saving')

    try {
      await save(payload)
      // Anything typed while this was in flight stays dirty, so the
      // acknowledgement of an older value cannot mark a newer one saved.
      for (const key of keys) {
        if (latest.current[key] === payload[key]) dirty.current.delete(key)
      }
      setError(null)
      setState(dirty.current.size === 0 ? 'clean' : 'pending')
      remember()
    } catch (cause) {
      setState('failed')
      setError(cause instanceof Error ? cause.message : 'Could not save that answer.')
    }
  }, [responseId, save, remember])

  const schedule = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      timer.current = null
      void send()
    }, IDLE_MS)
  }, [send])

  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current)
    },
    [],
  )

  const set = useCallback(
    (key: string, value: ReportAnswer) => {
      latest.current = { ...latest.current, [key]: value }
      dirty.current.add(key)
      setAnswers(latest.current)
      setState('pending')
      remember()
      schedule()
    },
    [remember, schedule],
  )

  const setMany = useCallback(
    (values: Record<string, ReportAnswer>) => {
      if (Object.keys(values).length === 0) return

      latest.current = { ...latest.current, ...values }
      for (const key of Object.keys(values)) dirty.current.add(key)
      setAnswers(latest.current)
      setState('pending')
      remember()
      schedule()
    },
    [remember, schedule],
  )

  const flush = useCallback(async () => {
    if (timer.current !== null) {
      clearTimeout(timer.current)
      timer.current = null
    }
    await send()
  }, [send])

  // Read by the two effects below, which must fire on a report or a network
  // event rather than every time a mutation object changes identity, but must
  // still send with the current closure when they do.
  const sender = useRef(send)
  sender.current = send

  /**
   * Recovering a draft the server was never told about.
   *
   * Runs once per report, after the server's answers have seeded the screen. Only
   * the keys the device recorded as outstanding are restored — the rest of the
   * stored answers are the server's own, and a stale copy of those would undo an
   * office correction made while the phone was in a paddock.
   *
   * Restored keys go back in dirty and are sent immediately, so the recovery is
   * over in one request rather than waiting for the technician to touch a field.
   */
  useEffect(() => {
    if (responseId === null) return

    let live = true

    void readDeviceDraft(responseId).then((stored) => {
      if (!live || stored === null) return

      const restore = stored.dirty.filter((key) => key in stored.answers)
      if (restore.length === 0) return void clearDeviceDraft(responseId)

      const merged = { ...latest.current }
      for (const key of restore) merged[key] = stored.answers[key] ?? null

      latest.current = merged
      for (const key of restore) dirty.current.add(key)
      setAnswers(merged)
      setRecovered(restore.length)
      setPersisted(true)
      setState('pending')
      void sender.current()
    })

    return () => {
      live = false
    }
  }, [responseId])

  /**
   * Coming back into signal.
   *
   * The browser fires `online` when the interface comes back, which is the one
   * moment a retry is near-certain to succeed and the technician has not touched
   * the screen to trigger one. Cheap to attempt: `send` returns immediately when
   * nothing is outstanding.
   */
  useEffect(() => {
    const retry = () => void sender.current()
    window.addEventListener('online', retry)
    return () => window.removeEventListener('online', retry)
  }, [])

  return {
    answers,
    set,
    setMany,
    flush,
    state,
    outstanding: state === 'pending' || state === 'saving' || state === 'failed',
    persisted,
    recovered,
    error,
  }
}
